/**
 * Material Cache Hook
 * Pre-creates all materials for all LOD levels to enable instant switching
 */

import { useEffect, useState, useMemo, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { createEarthMaterial } from '../materials/earthTiledMaterial'
import { TILE_GRID } from '../textures/tileConfig'
import materialConfig from '../config/materialConfig.json'

export function useMaterialCache(lodLevels, preloadComplete) {
  const { gl } = useThree()
  const isWebGPU = gl.isWebGPURenderer || false
  const [materialCache, setMaterialCache] = useState({})
  const [isReady, setIsReady] = useState(false)
  const materialCacheRef = useRef({})

  useEffect(() => {
    if (!preloadComplete) return

    let isMounted = true
    const newCache = {}

    async function createAllMaterials() {
      
      for (const lod of lodLevels) {
        const quality = lod.name
        const gridConfig = TILE_GRID[quality]

        try {
          // High-detail LODs (LOD_0, LOD_1) are only used when zoomed in close to the surface.
          // At that distance the inner atmosphere fresnel effect looks wrong, so suppress it.
          const isHighDetail = quality === 'LOD_0' || quality === 'LOD_1'
          const innerAtmosphereConfig = isHighDetail
            ? { innerAtmosphereIntensity: 0, innerAtmosphereFresnelPower: materialConfig.innerAtmosphereFresnelPower }
            : { innerAtmosphereIntensity: materialConfig.innerAtmosphereIntensity, innerAtmosphereFresnelPower: materialConfig.innerAtmosphereFresnelPower }

          // Create main Earth material
          const earthMaterial = await createEarthMaterial({
            tileTextures: [], // Empty arrays - will use cache
            normalTextures: [],
            specularTextures: [],
            nightmapTextures: [],
            cloudTextures: [],
            columns: gridConfig.columns,
            rows: gridConfig.rows,
            isWebGPU,
            quality,
            innerAtmosphereConfig,
          })

          // Create clouds material
          const cloudsMaterial = await createEarthMaterial({
            tileTextures: [],
            normalTextures: null,
            specularTextures: null,
            nightmapTextures: null,
            columns: gridConfig.columns,
            rows: gridConfig.rows,
            isWebGPU,
            quality,
            isClouds: true,
          })

          newCache[quality] = {
            earth: earthMaterial,
            clouds: cloudsMaterial,
          }
          
        } catch (error) {
          // Silent error handling
        }
      }

      if (isMounted) {
        setMaterialCache(newCache)
        materialCacheRef.current = newCache
        setIsReady(true)
      } else {
        // Cleanup if unmounted - dispose materials only, NOT cached textures
        // Textures are managed by textureCache.js and disposing them here would
        // cause use-after-free bugs when other components access them
        Object.entries(newCache).forEach(([quality, { earth, clouds }]) => {
          if (earth) {
            // Clear userData references but don't dispose (textures are in shared cache)
            earth.userData.textureArray = null
            earth.userData.normalArray = null
            earth.userData.specularArray = null
            earth.userData.nightmapArray = null
            earth.dispose()
          }
          if (clouds) {
            clouds.userData.textureArray = null
            clouds.dispose()
          }
        })
      }
    }

    createAllMaterials()

    return () => {
      isMounted = false
      // Dispose materials only - DO NOT dispose texture arrays as they're managed by textureCache
      // Disposing cached textures here would cause use-after-free bugs when other components 
      // access them from the shared cache
      Object.entries(materialCacheRef.current).forEach(([quality, { earth, clouds }]) => {
        if (earth) {
          // Clear userData references but don't dispose (textures are in shared cache)
          earth.userData.textureArray = null
          earth.userData.normalTextureArray = null
          earth.userData.normalArray = null
          earth.userData.specularTextureArray = null
          earth.userData.specularArray = null
          earth.userData.nightmapTextureArray = null
          earth.userData.nightmapArray = null
          earth.dispose()
        }
        if (clouds) {
          clouds.userData.textureArray = null
          clouds.dispose()
        }
      })
      materialCacheRef.current = {}
    }
  }, [lodLevels, isWebGPU, preloadComplete])

  return { materialCache, isReady }
}

export default useMaterialCache